import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import Icon from './Icon';
import { toast } from 'react-toastify';
import { suivisAPI } from '../services/api';

const ModalSuiviMultiNiveaux = ({ isOpen, onClose, apprenant, onSuccess }) => {
  const niveaux = ['M+0', 'M+3', 'M+6'];
  
  const [activeNiveau, setActiveNiveau] = useState('M+0');
  const [suiviForm, setSuiviForm] = useState({
    dateContact: new Date().toISOString().split('T')[0],
    typeContact: '',
    entreprise: apprenant?.entreprise || '',
    poste: apprenant?.poste || '',
    typeContrat: apprenant?.typeContrat || '',
    salaire: apprenant?.salaire || '',
    salaireTranche: apprenant?.salaireTranche || '',
    etatEmploi: 'en_emploi',
    appreciation: '',
    note: 0,
    commentaire: ''
  });
  
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [loading, setLoading] = useState(false);
  const [suivisExistants, setSuivisExistants] = useState({});

  // Charger les suivis existants
  useEffect(() => {
    setActiveNiveau('M+0'); // ← ajouter
    setHoveredStar(0);      // ← ajouter si tu as hoveredStar
    fetchSuivisExistants();
    if (apprenant && isOpen) {
      fetchSuivisExistants();
    }
  }, [apprenant, isOpen]);

  const fetchSuivisExistants = async () => {
  // ← Ajouter ces resets au début
  setSuivisExistants({});
  setRating(0);
  setSuiviForm({
    dateContact: new Date().toISOString().split('T')[0],
    typeContact: '',
    entreprise: apprenant?.entreprise || '',
    poste: apprenant?.poste || '',
    typeContrat: apprenant?.typeContrat || '',
    salaire: apprenant?.salaire || '',
    salaireTranche: apprenant?.salaireTranche || '',
    etatEmploi: 'en_emploi',
    appreciation: '',
    note: 0,
    commentaire: ''
  });
    try {
      const response = await suivisAPI.getMultiNiveaux(apprenant._id);
      const suivisMap = {};
      
      response.data.suivisMultiNiveaux?.forEach(s => {
        suivisMap[s.niveau] = s;
      });
      
      setSuivisExistants(suivisMap);
      
      // Charger les données du premier niveau existant ou M+0
      if (suivisMap[activeNiveau]) {
        setSuiviForm(prev => ({
          ...prev,
          ...suivisMap[activeNiveau],
          dateContact: suivisMap[activeNiveau].dateContact?.split('T')[0]
        }));
        setRating(suivisMap[activeNiveau].note || 0);
      } else {
        // Réinitialiser le formulaire pour un nouveau suivi
        setSuiviForm({
          dateContact: new Date().toISOString().split('T')[0],
          typeContact: '',
          entreprise: apprenant?.entreprise || '',
          poste: apprenant?.poste || '',
          typeContrat: apprenant?.typeContrat || '',
          salaire: apprenant?.salaire || '',
          salaireTranche: apprenant?.salaireTranche || '',
          etatEmploi: 'en_emploi',
          appreciation: '',
          note: 0,
          commentaire: ''
        });
        setRating(0);
      }
    } catch (error) {
      console.error('Erreur chargement suivis existants:', error);
    }
  };

  const handleNiveauChange = (niveau) => {
    setActiveNiveau(niveau);
    setHoveredStar(0);
    
    // Charger les données du niveau sélectionné
    if (suivisExistants[niveau]) {
      setSuiviForm(prev => ({
        ...prev,
        ...suivisExistants[niveau],
        dateContact: suivisExistants[niveau].dateContact?.split('T')[0]
      }));
      setRating(suivisExistants[niveau].note || 0);
    } else {
      // Réinitialiser pour un nouveau suivi
      setSuiviForm({
        dateContact: new Date().toISOString().split('T')[0],
        typeContact: '',
        entreprise: apprenant?.entreprise || '',
        poste: apprenant?.poste || '',
        typeContrat: apprenant?.typeContrat || '',
        salaire: apprenant?.salaire || '',
        salaireTranche: apprenant?.salaireTranche || '',
        etatEmploi: 'en_emploi',
        appreciation: '',
        note: 0,
        commentaire: ''
      });
      setRating(0);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSuiviForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!suiviForm.typeContact) {
      toast.error('Veuillez sélectionner un type de contact');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        apprenantId: apprenant._id,
        niveau: activeNiveau,
        ...suiviForm,
        note: rating,
        salaire: suiviForm.salaire ? parseInt(suiviForm.salaire) : null
      };

      await suivisAPI.createMultiNiveaux(payload);
      
      toast.success(`Suivi ${activeNiveau} enregistré avec succès !`);
      
      // Recharger les données
      fetchSuivisExistants();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Erreur enregistrement suivi:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const getNiveauStatus = (niveau) => {
    if (suivisExistants[niveau]) {
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">✓ Suivi</span>;
    }
    return <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">À faire</span>;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center">
          <Icon name="suivi" size={20} className="mr-2" />
          Suivi Multi-Niveaux d'Insertion
        </span>
      }
      size="lg"
    >
      {apprenant && (
        <div className="space-y-6">
          {/* Informations de l'apprenant */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="font-medium text-lg">{apprenant.nom}</p>
            <p className="text-sm text-gray-600">
              {apprenant.entreprise || '-'} • {apprenant.poste || '-'}
            </p>
          </div>

          {/* Sélection des niveaux */}
          <div>
            <h3 className="font-medium mb-3 flex items-center">
              <Icon name="liste" size={18} className="mr-2" />
              Niveaux de Suivi
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {niveaux.map(niveau => (
                <button
                  key={niveau}
                  onClick={() => handleNiveauChange(niveau)}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    activeNiveau === niveau
                      ? 'border-red-600 bg-red-50'
                      : 'border-gray-200 bg-white hover:border-red-300'
                  }`}
                >
                  <p className="font-bold text-sm">{niveau}</p>
                  {getNiveauStatus(niveau)}
                </button>
              ))}
            </div>
          </div>

          {/* Formulaire du suivi actif */}
          <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
            <FormInput
              label="Date de contact"
              name="dateContact"
              type="date"
              value={suiviForm.dateContact}
              onChange={handleInputChange}
              required
            />

            <FormSelect
              label="Type de contact"
              name="typeContact"
              value={suiviForm.typeContact}
              onChange={handleInputChange}
              required
              options={[
                { value: '', label: '-' },
                { value: 'telephone', label: '📞 Téléphone' },
                { value: 'email', label: '📧 Email' },
                { value: 'visite', label: '🏢 Visite' }
              ]}
            />

            <FormSelect
              label="État de l'emploi"
              name="etatEmploi"
              value={suiviForm.etatEmploi}
              onChange={handleInputChange}
              options={[
                { value: 'en_emploi', label: '✓ En emploi' },
                { value: 'en_recherche', label: '🔍 En recherche' },
                { value: 'autre', label: 'Autre' }
              ]}
            />

            {/* Données emploi */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h4 className="font-medium flex items-center">
                <Icon name="success" size={16} className="mr-2 text-red-600" />
                Informations Emploi
              </h4>
              
              <FormInput
                label="Entreprise"
                name="entreprise"
                value={suiviForm.entreprise}
                onChange={handleInputChange}
              />

              <FormInput
                label="Poste"
                name="poste"
                value={suiviForm.poste}
                onChange={handleInputChange}
              />

              <FormSelect
                label="Type de contrat"
                name="typeContrat"
                value={suiviForm.typeContrat}
                onChange={handleInputChange}
                options={[
                  { value: '', label: '-' },
                  { value: 'CDI', label: 'CDI' },
                  { value: 'CDD', label: 'CDD' },
                  { value: 'Stage', label: 'Stage' },
                  { value: 'Alternance', label: 'Alternance' },
                  { value: 'Freelance', label: 'Freelance' },
                  { value: 'Indépendant', label: 'Indépendant' }
                ]}
              />

              <div className="grid grid-cols-2 gap-3">
                <FormInput
                  label="Salaire"
                  name="salaire"
                  type="number"
                  value={suiviForm.salaire}
                  onChange={handleInputChange}
                />

                <FormSelect
                  label="Tranche salariale"
                  name="salaireTranche"
                  value={suiviForm.salaireTranche}
                  onChange={handleInputChange}
                  options={[
                    { value: '', label: '-' },
                    { value: '< 100000', label: 'Moins de 100 000 FCFA' },
                    { value: '100000-200000', label: '100 000 - 200 000 FCFA' },
                    { value: '200000-300000', label: '200 000 - 300 000 FCFA' },
                    { value: '300000-500000', label: '300 000 - 500 000 FCFA' },
                    { value: '> 500000', label: 'Plus de 500 000 FCFA' }
                  ]}
                />
              </div>
            </div>

            {/* Appréciation et Note */}
            <div className="space-y-3">
              <div>
                <label htmlFor="rating" className="block text-sm font-medium mb-2">
                  Note de satisfaction (1-5)
                </label>
                <div className="flex gap-2">
                 {[1, 2, 3, 4, 5].map(star => (
  <button
    key={star}
    type="button"
    onClick={() => {
      setRating(star);
      setSuiviForm(prev => ({ ...prev, note: star }));
    }}
    onMouseEnter={() => setHoveredStar(star)}
    onMouseLeave={() => setHoveredStar(0)}
    className={`text-3xl transition-transform duration-150 ${
      star <= (hoveredStar || rating) ? 'text-yellow-400' : 'text-gray-300'
    } ${
      star === rating ? 'scale-125' : 'hover:scale-125'
    }`}
    aria-label={`Noter ${star} étoile${star > 1 ? 's' : ''}`}
  >
    ⭐
  </button>
))}
                </div>
              </div>

              <FormSelect
                label="Appréciation générale"
                name="appreciation"
                value={suiviForm.appreciation}
                onChange={handleInputChange}
                options={[
                  { value: '', label: '-' },
                  { value: 'excellent', label: '🌟 Excellent' },
                  { value: 'tres_bon', label: '👍 Très bon' },
                  { value: 'bon', label: '👌 Bon' },
                  { value: 'moyen', label: '😐 Moyen' },
                  { value: 'insuffisant', label: '👎 Insuffisant' }
                ]}
              />
            </div>

            {/* Commentaires */}
            <div>
              <label htmlFor="commentaire" className="block text-sm font-medium mb-2">
                Commentaires
              </label>
              <textarea
                id="commentaire"
                name="commentaire"
                value={suiviForm.commentaire}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Observations, remarques..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {loading ? 'Enregistrement...' : `Enregistrer ${activeNiveau}`}
              </button>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
};

export default ModalSuiviMultiNiveaux;
